import { supabase } from './supabase';

const BUCKET = 'lab-results';

const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
];

export interface LabResultAttachment {
  id: string;
  labResultId: string;
  fileName: string;
  filePath: string;
  contentType: string;
  fileSize?: number;
  createdAt: string;
}

const toCamelCase = (row: Record<string, unknown>): LabResultAttachment => ({
  id: String(row.id),
  labResultId: String(row.lab_result_id),
  fileName: String(row.file_name),
  filePath: String(row.file_path),
  contentType: String(row.content_type),
  fileSize: row.file_size != null ? Number(row.file_size) : undefined,
  createdAt: String(row.created_at),
});

export const labResultAttachmentsService = {
  async getByLabResultIds(labResultIds: string[]): Promise<Record<string, LabResultAttachment[]>> {
    if (labResultIds.length === 0) return {};
    const { data, error } = await supabase
      .from('lab_result_attachments')
      .select('*')
      .in('lab_result_id', labResultIds);

    if (error) throw error;
    const byId: Record<string, LabResultAttachment[]> = {};
    for (const row of data || []) {
      const id = String(row.lab_result_id);
      if (!byId[id]) byId[id] = [];
      byId[id].push(toCamelCase(row));
    }
    return byId;
  },

  async getByLabResultId(labResultId: string): Promise<LabResultAttachment[]> {
    const { data, error } = await supabase
      .from('lab_result_attachments')
      .select('*')
      .eq('lab_result_id', labResultId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(toCamelCase);
  },

  async upload(
    userId: string,
    labResultId: string,
    file: File
  ): Promise<LabResultAttachment> {
    const ext = file.name.split('.').pop() || 'bin';
    const path = `${userId}/${labResultId}/${crypto.randomUUID()}.${ext}`;

    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error('Nieobsługiwany typ pliku. Dozwolone: PDF, JPG, PNG, WebP, HEIC.');
    }
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('Plik za duży (max 10 MB).');
    }

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, { contentType: file.type, upsert: false });

    if (uploadError) throw uploadError;

    const { data: row, error } = await supabase
      .from('lab_result_attachments')
      .insert({
        lab_result_id: labResultId,
        user_id: userId,
        file_path: path,
        file_name: file.name,
        content_type: file.type,
        file_size: file.size,
      })
      .select()
      .single();

    if (error) {
      await supabase.storage.from(BUCKET).remove([path]);
      throw error;
    }
    return toCamelCase(row);
  },

  async getSignedUrl(path: string, expiresIn = 3600): Promise<string> {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(path, expiresIn);

    if (error) throw error;
    return data.signedUrl;
  },

  async delete(attachment: LabResultAttachment, userId: string): Promise<void> {
    const { error: dbError } = await supabase
      .from('lab_result_attachments')
      .delete()
      .eq('id', attachment.id)
      .eq('user_id', userId);

    if (dbError) throw dbError;

    await supabase.storage.from(BUCKET).remove([attachment.filePath]);
  },
};
